/**
 * Custom Chart.js plugins for the Polling section.
 */

/**
 * Draws dashed vertical lines on a chart (used for sunrise/sunset markers).
 *
 * Usage in chartOptions:
 *   plugins: { verticalLines: { lines: [{ index: 5, color: 'rgba(...)' }] } }
 */
export const verticalLinesPlugin = {
  id: 'verticalLines',
  afterDraw(chart, _args, options) {
    if (!options?.lines?.length) return
    const { ctx, chartArea, scales } = chart
    if (!scales?.x || !chartArea) return

    options.lines.forEach(({ index, color }) => {
      if (index < 0 || index >= (chart.data.labels?.length ?? 0)) return
      const x = scales.x.getPixelForValue(index)

      ctx.save()
      ctx.beginPath()
      ctx.setLineDash([5, 4])
      ctx.strokeStyle = color
      ctx.lineWidth = 1.5
      ctx.moveTo(x, chartArea.top)
      ctx.lineTo(x, chartArea.bottom)
      ctx.stroke()
      ctx.restore()
    })
  },
}

/**
 * Draws horizontal dashed threshold lines on a chart (used for voltage limits).
 *
 * Usage in chartOptions:
 *   plugins: { voltageThreshold: { thresholds: [{ value: 250, color: 'rgba(...)', label: '250 V' }] } }
 */
export const voltageThresholdPlugin = {
  id: 'voltageThreshold',
  afterDraw(chart, _args, options) {
    if (!options?.thresholds?.length) return
    const { ctx, chartArea, scales } = chart
    if (!scales?.y || !chartArea) return

    options.thresholds.forEach(({ value, color, label }) => {
      const y = scales.y.getPixelForValue(value)
      if (y < chartArea.top || y > chartArea.bottom) return

      ctx.save()
      ctx.beginPath()
      ctx.setLineDash([4, 4])
      ctx.strokeStyle = color
      ctx.lineWidth = 1.25
      ctx.moveTo(chartArea.left, y)
      ctx.lineTo(chartArea.right, y)
      ctx.stroke()

      if (label) {
        ctx.setLineDash([])
        ctx.fillStyle = color
        ctx.font = '10px sans-serif'
        ctx.textAlign = 'right'
        ctx.textBaseline = 'bottom'
        ctx.fillText(label, chartArea.right - 4, y - 2)
      }
      ctx.restore()
    })
  },
}

/**
 * Shades x-axis regions corresponding to congestion intervals.
 * Each interval references labels (HH:mm) that must exist in chart.data.labels.
 *
 * Usage in chartOptions:
 *   plugins: { congestionOverlay: { intervals: [{ fromLabel: '13:15', toLabel: '15:00', severity: 'warning' }] } }
 */
export const congestionOverlayPlugin = {
  id: 'congestionOverlay',
  beforeDatasetsDraw(chart, _args, options) {
    if (!options?.intervals?.length) return
    const { ctx, chartArea, scales, data } = chart
    if (!scales?.x || !chartArea || !data?.labels?.length) return

    const labels = data.labels
    const fills = {
      warning: 'rgba(245, 158, 11, 0.22)',
      critical: 'rgba(239, 68, 68, 0.28)',
    }
    const strokes = {
      warning: 'rgba(245, 158, 11, 0.9)',
      critical: 'rgba(239, 68, 68, 0.95)',
    }

    // Voltage and production sample rates can differ, so map interval labels
    // to the nearest production HH:mm label within 15 minutes.
    const toMins = t => {
      const [h, m] = t.split(':').map(Number)
      return h * 60 + m
    }
    const labelMins = labels.map(toMins)
    const nearestIdx = label => {
      const target = toMins(label)
      let best = -1
      let bestDiff = Infinity
      for (let i = 0; i < labelMins.length; i++) {
        const diff = Math.abs(labelMins[i] - target)
        if (diff < bestDiff) { bestDiff = diff; best = i }
      }
      return bestDiff <= 15 ? best : -1
    }

    options.intervals.forEach(({ fromLabel, toLabel, severity }) => {
      const fromIdx = nearestIdx(fromLabel)
      const toIdx = nearestIdx(toLabel)
      if (fromIdx < 0 || toIdx < 0) return

      const xFrom = scales.x.getPixelForValue(fromIdx)
      const xTo = scales.x.getPixelForValue(toIdx)
      const pad = 8
      const left = Math.max(chartArea.left, Math.min(xFrom, xTo) - pad)
      const right = Math.min(chartArea.right, Math.max(xFrom, xTo) + pad)
      const width = Math.max(right - left, 4)

      ctx.save()
      ctx.fillStyle = fills[severity] ?? fills.warning
      ctx.fillRect(left, chartArea.top, width, chartArea.bottom - chartArea.top)
      // Top marker so narrow intervals remain visible.
      ctx.strokeStyle = strokes[severity] ?? strokes.warning
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(left, chartArea.top + 1)
      ctx.lineTo(left + width, chartArea.top + 1)
      ctx.stroke()
      ctx.restore()
    })
  },
}
